import { FC, PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import type { ChartResizeEdge } from './useChartCardResize';

type Props = {
  edge: ChartResizeEdge;
  onPointerDown: (edge: ChartResizeEdge, event: PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
};

/**
 * Граница карточки, за которую диаграмму растягивают: правая — по ширине, нижняя — по высоте.
 *
 * Видимым ручку делает короткий знак посреди стороны, а не заливка вдоль всего края: край,
 * подсвеченный целиком, читается как полоса прокрутки — и пользователь принимал его за неё у
 * диаграммы, содержимое которой поместилось целиком. Целиться при этом можно по-прежнему во всю
 * сторону — место захвата не изменилось.
 *
 * Ширина задаётся только на широком экране: на узком дашборд раскладывается в одну колонку,
 * и доля ширины там ничего не меняет.
 */
export const ChartResizeHandle: FC<Props> = ({ edge, onPointerDown, onPointerMove, onPointerUp }) => {
  const { t } = useTranslation('qualityDashboard');

  const label = edge === 'width' ? t('presets.editor.resizeWidth') : t('presets.editor.resizeHeight');

  return (
    <div
      role="button"
      tabIndex={-1}
      aria-label={label}
      className={cn(
        'group/resize absolute z-10 items-center justify-center opacity-0 transition-opacity group-hover/chart:opacity-100',
        edge === 'width'
          ? 'inset-y-2 right-0 hidden w-2 cursor-col-resize lg:flex'
          : 'inset-x-2 bottom-0 flex h-2 cursor-row-resize',
      )}
      onPointerDown={(event) => onPointerDown(edge, event)}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div
        className={cn(
          'rounded-full bg-primary/40 transition-colors group-hover/resize:bg-primary',
          edge === 'width' ? 'h-8 w-1' : 'h-1 w-8',
        )}
      />
    </div>
  );
};
